import { get as baseGet, set as baseSet, remove as baseRemove } from "local-storage"

const STORAGE_KEY = "ObikeStore_sessionConfig"
const ENTITY_ID_KEY = "ObikeStore_id"
const USER_ID_KEY = "ObikeStore_userid"
const REMEMBER_MAIL_KEY = "ObikeStore_rememberMail"
const REMEMBER_OPERATOR = "ObikeStore_currentOperator"

const HISTORY_SAVED_BIKES = "ObikeStore_historySavedBikes"
const HISTORY_PRESAVED_BIKES = "ObikeStore_historyPreSavedBikes"

// The entityId is used in the storage name on history page. So we have to do the same here
const getStorageNameFromHistorySavedBikesWithEntityId = (entityId) => `${HISTORY_SAVED_BIKES}_${entityId}`
const getStorageNameFromHistoryPresavedBikesWithEntityId = (entityId) => `${HISTORY_PRESAVED_BIKES}_${entityId}`

const TIMER_KEY = "ObikeStore_starting_timer"

const defaultKeyValues = (entityId) => {
    return {
        // init with truthy values
        [STORAGE_KEY]: " ",
        [ENTITY_ID_KEY]: " ",
        [USER_ID_KEY]: " ",
        [REMEMBER_MAIL_KEY]: " ",
        [getStorageNameFromHistorySavedBikesWithEntityId(entityId)]: "[]",
        [getStorageNameFromHistoryPresavedBikesWithEntityId(entityId)]: "[]"
    }
}


const localStorageService = {
    get(key, defaultValue = null) {
        const value = baseGet(key)

        return value === null ? defaultValue : value
    },

    set(key, value) {
        return baseSet(key, value)
    },

    remove(key) {
        baseRemove(key)
    }
}

const sessionStorageService = {
    get(key, defaultValue = null) {
        const value = sessionStorage.getItem(key)

        return value === null ? defaultValue : value
    },

    set(key, value) {
        return sessionStorage.setItem(key, value)
    },

    remove(key) {
        sessionStorage.removeItem(key)
    },

    clear() {
        sessionStorage.clear()
    },

    initSession({ entity_id, user_id }) {
        for (const [key, value] of Object.entries(defaultKeyValues(entity_id))) this.set(key, value)
        if (entity_id) this.set(ENTITY_ID_KEY, entity_id)
        if (user_id) this.set(USER_ID_KEY, user_id)
    }
}

const browserStorageService = {

    defaultTarget: "localStorage",

    // NB: in this class the methods have an optional "priority" param.
    // By default the used storage is the localstorage.
    // The priority param allows us to first search if the key exists in another
    // given storage (mainly the session storage). If so, the given storage is used.
    // This feat. is used to allows support to connect as a given account: all the key are
    // then stored in the session storage.

    get(key, options = {}) {
        const target = options?.target ?? this.defaultTarget
        const priority = options?.priority

        const localStorageKey = localStorageService.get(key)
        const sessionStorageKey = sessionStorageService.get(key)

        if (target === "sessionStorage") {
            if (priority === "localStorage") {
                return localStorageKey ?? sessionStorageKey
            } else {
                return sessionStorageKey
            }
        } else if (target === "localStorage") {
            if (priority === "sessionStorage") {
                return sessionStorageKey ?? localStorageKey
            } else {
                return localStorageKey
            }
        }
    },

    set(key, value, options = {}) {
        const target = options?.target ?? this.defaultTarget
        const priority = options?.priority

        const localStorageKey = localStorageService.get(key)
        const sessionStorageKey = sessionStorageService.get(key)
        if (target === "sessionStorage") {
            // if priority is given to localStorage and the key exist in the storage
            if (priority === "localStorage" && localStorageKey) {
                return localStorageService.set(key, value)
            } else {
                return sessionStorageService.set(key, value)
            }
        } else if (target === "localStorage") {
            // if priority is given to sessionStorage and the key exist in the storage
            if (priority === "sessionStorage" && sessionStorageKey) {
                return sessionStorageService.set(key, value)
            } else {
                return localStorageService.set(key, value)
            }
        }
    },

    remove(key, options = {}) {
        const target = options?.target ?? this.defaultTarget
        const priority = options?.priority

        const localStorageKey = localStorageService.get(key)
        const sessionStorageKey = sessionStorageService.get(key)
        if (target === "sessionStorage") {
            // if priority is given to localStorage and the key exist in the storage
            if (priority === "localStorage" && localStorageKey) {
                return localStorageService.remove(key)
            } else {
                return sessionStorageService.remove(key)
            }
        } else if (target === "localStorage") {
            // if priority is given to sessionStorage and the key exist in the storage
            if (priority === "sessionStorage" && sessionStorageKey) {
                return sessionStorageService.remove(key)
            } else {
                return localStorageService.remove(key)
            }
        }
    }
}

export {
    browserStorageService as default,
    localStorageService,
    sessionStorageService,
    REMEMBER_MAIL_KEY,
    STORAGE_KEY,
    ENTITY_ID_KEY,
    USER_ID_KEY,
    REMEMBER_OPERATOR,
    HISTORY_SAVED_BIKES,
    HISTORY_PRESAVED_BIKES,
    TIMER_KEY
}
